/** Documentos emitidos: listado con filtros, detalle con líneas/pagos/retenciones, cobrar saldo (crédito), reimprimir. */
import { ESTADO_RETENCION_ETIQUETA, formatearExacto, formatearTasa, METODO_PAGO_ETIQUETA, METODO_VUELTO_BS_ETIQUETA, Money, type ClienteDTO, type DocumentoDTO, type PagoLineaInput, type VueltoEntrada } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { FileX, Layers, Printer, UserRoundPen, Wallet } from 'lucide-react';
import { Fragment, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { Adjuntos } from '@/componentes/Adjuntos.tsx';
import { CopiarWhatsApp } from '@/componentes/CopiarWhatsApp.tsx';
import { CorreosEnviados } from '@/componentes/CorreosEnviados.tsx';
import { Dinero } from '@/componentes/Dinero.tsx';
import { EnviarCorreo } from '@/componentes/EnviarCorreo.tsx';
import { EstadoQuery, Vacio } from '@/componentes/estados.tsx';
import { FiltroFechas } from '@/componentes/FiltroFechas.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Filtro } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { get, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { veTasa2 } from '@/lib/tasa2.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { cabeceraDeCombo, tituloCombo } from '@/modulos/combos/documento.ts';
import { useDocumento, useDocumentos, usePagarDocumento } from './api.ts';
import { CobroDialog } from './CobroDialog.tsx';
import { DialogoOtroNombre } from './OtroNombre.tsx';
import { DialogoHojaEnBlanco, DialogoReemitir } from './PapelFiscal.tsx';

export const ETIQUETA_TIPO: Record<string, string> = { FAC: 'Factura', NOTA: 'Nota', NC: 'Nota de crédito', ND: 'Nota de débito', CAM: 'Cambio' };
const ESTADO_PAGO: Record<string, { texto: string; v: 'ok' | 'pendiente' | 'critico' | 'neutro' }> = { pagado: { texto: 'Pagado', v: 'ok' }, parcial: { texto: 'Parcial', v: 'pendiente' }, pendiente: { texto: 'Pendiente', v: 'critico' } };
/** Modalidad de la retención con su nombre (como en el POS), no el interno (re-auditoría 2026-10-18, ronda 2, hallazgo 1-H15). */
const MODALIDAD_RETENCION: Record<'base25' | 'completo', string> = { base25: 'Retención: base + 25 % del IVA', completo: 'Retención: pago completo (reintegro después)' };
const ESTADO_ENTREGA: Record<string, { texto: string; v: 'ok' | 'pendiente' | 'critico' | 'neutro' }> = { entregado: { texto: 'Entregado', v: 'ok' }, parcial: { texto: 'Entrega parcial', v: 'pendiente' }, pendiente: { texto: 'Por entregar', v: 'pendiente' }, no_aplica: { texto: '—', v: 'neutro' } };

export function tasasDe(d: DocumentoDTO) {
  return { tasa_bcv: d.tasa_bcv, tasa_paralela: d.tasa_paralela, factor_divisa: d.factor_divisa };
}

export function Documentos() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [tipo, setTipo] = useState('');
  const [estadoPago, setEstadoPago] = useState('');
  const [estadoEntrega, setEstadoEntrega] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [pagina, setPagina] = useState(1);
  const [estado, setEstado] = useState('');
  const [hojaBlanco, setHojaBlanco] = useState(false);
  const { puede } = useAuth();
  const lista = useDocumentos({ estado: estado || undefined, q: q || undefined, tipo: tipo || undefined, estado_pago: estadoPago || undefined, estado_entrega: estadoEntrega || undefined, desde: desde || undefined, hasta: hasta || undefined, pagina, limite: 25 });
  return (
    <>
      <TituloPagina
        titulo="Documentos"
        subtitulo="Facturas, notas, NC/ND y cambios de la sucursal. Los documentos emitidos no se editan ni se borran."
        acciones={puede('ventas.emitir') ? <Button variant="outline" onClick={() => setHojaBlanco(true)} data-testid="doc-hoja-blanco"><FileX /> Anular hoja en blanco</Button> : undefined}
      />
      <DialogoHojaEnBlanco abierto={hojaBlanco} cerrar={() => setHojaBlanco(false)} />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <Input placeholder="N°, control, cliente o RIF…" value={q} onChange={(e) => { setQ(e.target.value); setPagina(1); }} className="w-72" data-testid="doc-buscar" />
        <FiltroFechas desde={desde} hasta={hasta} onChange={(d, h) => { setDesde(d); setHasta(h); setPagina(1); }} testid="doc" />
        {/* Cada filtro con su rótulo (auditoría de frontend FE-15) */}
        <Filtro etiqueta="Tipo">
          <Select value={tipo} onChange={(e) => { setTipo(e.target.value); setPagina(1); }} className="w-40" aria-label="Tipo de documento" data-testid="doc-tipo">
            <option value="">Todos los tipos</option>
            {Object.entries(ETIQUETA_TIPO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
        </Filtro>
        <Filtro etiqueta="Pago">
          <Select value={estadoPago} onChange={(e) => { setEstadoPago(e.target.value); setPagina(1); }} className="w-40" aria-label="Estado del pago" data-testid="doc-estado-pago">
            <option value="">Pago: todos</option>
            <option value="pendiente">Pendiente</option>
            <option value="parcial">Parcial</option>
            <option value="pagado">Pagado</option>
          </Select>
        </Filtro>
        <Filtro etiqueta="Entrega">
          <Select value={estadoEntrega} onChange={(e) => { setEstadoEntrega(e.target.value); setPagina(1); }} className="w-44" aria-label="Estado de la entrega" data-testid="doc-estado-entrega">
            <option value="">Entrega: todas</option>
            <option value="pendiente">Por entregar</option>
            <option value="parcial">Parcial</option>
            <option value="entregado">Entregado</option>
          </Select>
        </Filtro>
        <Filtro etiqueta="Estado">
          {/* Más ancho: «Emitidos y anulados» se cortaba */}
          <Select value={estado} onChange={(e) => { setEstado(e.target.value); setPagina(1); }} className="w-52" aria-label="Estado del documento" data-testid="doc-estado">
            <option value="">Emitidos y anulados</option>
            <option value="emitido">Solo emitidos</option>
            <option value="anulado">Solo anulados</option>
          </Select>
        </Filtro>
      </div>
      <EstadoQuery cargando={lista.isLoading} error={lista.error} datos={lista.data} reintentar={() => void lista.refetch()}>
        {(d) =>
          d.datos.length === 0 ? (
            <Vacio titulo="Sin documentos" descripcion="Aún no hay documentos con esos filtros." />
          ) : (
            <div className="space-y-3">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Documento</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Pago</TableHead>
                    <TableHead>Entrega</TableHead>
                    <TableHead>Cajero</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.datos.map((x) => (
                    <TableRow key={x.id} className={`cursor-pointer ${x.estado === 'anulado' ? 'text-muted-foreground' : ''}`} onClick={() => navigate(`/documentos/${x.id}`)} data-testid={`doc-${x.numero_formateado}`}>
                      <TableCell>
                        <p className="font-medium">{x.tipo} {x.numero_formateado}{x.estado === 'anulado' ? <Badge variant="critico" className="ml-1">ANULADA</Badge> : null}</p>
                        {x.control_formateado ? <p className="text-[11px] text-muted-foreground">control {x.control_formateado}</p> : null}
                      </TableCell>
                      <TableCell>
                        <p>{x.cliente_razon_social}</p>
                        <p className="text-[11px] text-muted-foreground">{x.cliente_rif}</p>
                      </TableCell>
                      <TableCell>{formatearFechaHora(x.emitido_en)}</TableCell>
                      <TableCell className="text-right"><Dinero valor={x.total} bs={x.total_bs} doc={{ tasa_bcv: x.tasa_aplicada, tasa_paralela: x.tasa_aplicada }} /></TableCell>
                      <TableCell>
                        {x.estado === 'anulado' ? '—' : <Badge variant={ESTADO_PAGO[x.estado_pago]?.v ?? 'neutro'}>{ESTADO_PAGO[x.estado_pago]?.texto ?? x.estado_pago}</Badge>}
                        {x.estado !== 'anulado' && x.condicion === 'credito' ? <Badge variant="info" className="ml-1">crédito</Badge> : null}
                      </TableCell>
                      <TableCell>{x.estado === 'anulado' ? '—' : <Badge variant={ESTADO_ENTREGA[x.estado_entrega]?.v ?? 'neutro'}>{ESTADO_ENTREGA[x.estado_entrega]?.texto ?? x.estado_entrega}</Badge>}</TableCell>
                      <TableCell>{x.cajero_nombre}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{d.total} documentos</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>Anterior</Button>
                  <Button size="sm" variant="outline" disabled={pagina * d.limite >= d.total} onClick={() => setPagina((p) => p + 1)}>Siguiente</Button>
                </div>
              </div>
            </div>
          )
        }
      </EstadoQuery>
    </>
  );
}

export function DocumentoDetalle() {
  const { id } = useParams<{ id: string }>();
  const { puede, usuario } = useAuth();
  // La tasa 2 congelada y la tasa de cada pago en Bs solo las ve la dueña (pedido de la dueña 2026-10-19)
  const veTasa = veTasa2(usuario);
  const navigate = useNavigate();
  const doc = useDocumento(id ?? null);
  const pagar = usePagarDocumento(id ?? '');
  const [cobrando, setCobrando] = useState(false);
  const [reemitiendo, setReemitiendo] = useState(false);
  const [otroNombre, setOtroNombre] = useState(false);
  // Clave de idempotencia del cobro: nace al abrir el diálogo y se conserva en los reintentos (si el servidor cobró y
  // la respuesta se perdió, el segundo clic no registra otro pago)
  const [clave, setClave] = useState(() => crypto.randomUUID());
  // Saldo a favor del cliente: sirve como forma de pago también al cobrar un documento pendiente
  const porCobrar = !!doc.data && doc.data.estado !== 'anulado' && Money.parse(doc.data.saldo_pendiente).esPositivo() && puede('pagos.cobrar');
  const cliente = useQuery({ queryKey: ['clientes', 'detalle', doc.data?.cliente_id ?? ''], queryFn: () => get<ClienteDTO>(`/clientes/${doc.data!.cliente_id}`), enabled: porCobrar });
  return (
    <EstadoQuery cargando={doc.isLoading} error={doc.error} datos={doc.data} reintentar={() => void doc.refetch()}>
      {(d) => {
        const t = tasasDe(d);
        const anulado = d.estado === 'anulado';
        // Solo el papel fiscal (FAC, NC, ND) se anula y reemite, y solo el mismo día (el servidor valida día y caja)
        const hoy = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Caracas' });
        const reemitible = !anulado && ['FAC', 'NC', 'ND'].includes(d.tipo) && hoy.format(new Date(d.emitido_en)) === hoy.format(new Date()) && puede('ventas.emitir');
        // «Facturar a otro nombre» (spec 08, enmienda 2026-10-29): factura o nota pagada, sin notas ni cambios. La entrada la
        // tiene quien registra devoluciones; sin el permiso de la dueña, el servidor pide la autorización
        const aOtroNombre = !anulado && (d.tipo === 'FAC' || d.tipo === 'NOTA') && !d.sustituida_por && !d.tiene_devolucion && !Money.parse(d.saldo_pendiente).esPositivo() && puede('devoluciones.registrar');
        // Estado del retiro (Fase 1 de retiros): la factura o la nota que tiene material que entregar
        const conRetiro = !anulado && (d.tipo === 'FAC' || d.tipo === 'NOTA') && d.estado_entrega !== 'no_aplica';
        return (
          <>
            <TituloPagina
              titulo={`${ETIQUETA_TIPO[d.tipo]} ${d.numero_formateado}`}
              subtitulo={`${d.control_formateado ? `N° control ${d.control_formateado} · ` : ''}${formatearFechaHora(d.emitido_en)} · ${d.cajero_nombre} · ${d.sucursal_nombre}`}
              acciones={
                <>
                  <Button variant="outline" onClick={() => navigate('/documentos')}>Volver</Button>
                  {reemitible ? <Button variant="outline" onClick={() => setReemitiendo(true)} data-testid="doc-reemitir"><FileX /> Hoja dañada: anular y reemitir</Button> : null}
                  {aOtroNombre ? <Button variant="outline" onClick={() => setOtroNombre(true)} data-testid="doc-otro-nombre"><UserRoundPen /> {d.tipo === 'FAC' ? 'Facturar a otro nombre' : 'Pasar a otro nombre'}</Button> : null}
                  {!anulado ? (
                    <Button variant="outline" onClick={() => window.open(`/imprimir/${d.tipo}/${d.id}`, '_blank')} data-testid="doc-imprimir">
                      <Printer /> {d.impresiones > 0 ? 'Reimprimir' : 'Imprimir'}
                    </Button>
                  ) : null}
                  {/* Al correo registrado del cliente, con el formato del documento (pedido de la dueña 2026-10-19) */}
                  {!anulado ? <EnviarCorreo tipo="documento" id={d.id} /> : null}
                  {/* Lo comprado, lo retirado y lo pendiente, sin precios: al correo de la ficha o copiado para WhatsApp */}
                  {conRetiro ? <EnviarCorreo tipo="retiro" id={d.id} etiqueta="Enviar estado de retiro" /> : null}
                  {conRetiro && puede('inventario.entregas', 'lectura') ? <CopiarWhatsApp documentoId={d.id} /> : null}
                  {!anulado && Money.parse(d.saldo_pendiente).esPositivo() && puede('pagos.cobrar') ? (
                    <Button onClick={() => { setClave(crypto.randomUUID()); setCobrando(true); }} data-testid="doc-cobrar">
                      <Wallet /> Cobrar saldo $ {Money.parse(d.saldo_pendiente).toDisplay()}
                    </Button>
                  ) : null}
                </>
              }
            />
            {anulado ? (
              <div className="mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900" role="status" data-testid="doc-anulado">
                <b>ANULADA</b> por hoja dañada el {d.anulado_en ? formatearFechaHora(d.anulado_en) : ''}{d.anulado_por_nombre ? ` · ${d.anulado_por_nombre}` : ''}. Motivo: {d.motivo_anulacion}.
                {d.reemitido_como ? <> La venta sigue en <Link to={`/documentos/${d.reemitido_como.id}`} className="font-semibold underline" data-testid="doc-reemitido-como">{d.tipo} {d.reemitido_como.numero_formateado}</Link> (control {d.reemitido_como.control_formateado}).</> : null}
                {' '}Este número y su control salen en el libro de ventas en cero.
              </div>
            ) : null}
            {d.anulados.length > 0 ? (
              <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status" data-testid="doc-reemision">
                Reemitida por hoja dañada. Números anulados:{' '}
                {d.anulados.map((a, i) => (
                  <span key={a.id}>{i > 0 ? ', ' : ''}<Link to={`/documentos/${a.id}`} className="font-semibold underline">{a.numero_formateado}</Link> (control {a.control_formateado}; {a.motivo})</span>
                ))}
              </div>
            ) : null}
            {d.sustituida_por ? (
              <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status" data-testid="doc-sustituida">
                <b>Pasada a otro nombre.</b> La revirtió {d.derivados.filter((x) => x.tipo === 'NC' || x.tipo === 'CAM').map((x) => `${x.tipo === 'NC' ? 'la nota de crédito' : 'el Cambio'} ${x.numero_formateado}`).join(', ')} y la sustituye{' '}
                <Link to={`/documentos/${d.sustituida_por.id}`} className="font-semibold underline">{d.sustituida_por.tipo === 'FAC' ? 'la factura' : 'la nota'} {d.sustituida_por.numero_formateado}</Link> de {d.sustituida_por.cliente_razon_social}. Lo que faltaba por entregar se retira con esa.
              </div>
            ) : null}
            {d.sustituye ? (
              <div className="mb-3 rounded-lg border border-mg-verde/40 bg-mg-verde-claro/60 px-3 py-2 text-sm" role="status" data-testid="doc-sustituye">
                Sustituye a <Link to={`/documentos/${d.sustituye.id}`} className="font-semibold underline">{d.sustituye.tipo === 'FAC' ? 'la factura' : 'la nota'} {d.sustituye.numero_formateado}</Link> de {d.sustituye.cliente_razon_social} (cambio de nombre): misma información, pagada con el crédito de su nota de crédito. Lo entregado con aquella cuenta como entregado aquí.
              </div>
            ) : null}
            {d.observaciones ? <p className="mb-3 text-xs text-muted-foreground" data-testid="doc-observaciones">Observaciones: {d.observaciones}</p> : null}
            <DialogoReemitir key={d.id} doc={d} abierto={reemitiendo} cerrar={() => setReemitiendo(false)} />
            {aOtroNombre ? <DialogoOtroNombre key={`otro-${d.id}`} doc={d} abierto={otroNombre} cerrar={() => setOtroNombre(false)} /> : null}
            <div className={`mb-3 flex flex-wrap gap-2 ${anulado ? 'hidden' : ''}`}>
              <Badge variant={ESTADO_PAGO[d.estado_pago]?.v ?? 'neutro'}>{ESTADO_PAGO[d.estado_pago]?.texto}</Badge>
              <Badge variant={ESTADO_ENTREGA[d.estado_entrega]?.v ?? 'neutro'}>{ESTADO_ENTREGA[d.estado_entrega]?.texto}</Badge>
              {d.condicion === 'credito' ? <Badge variant="info">Crédito · vence {d.vence_en ? formatearFechaHora(d.vence_en, { hour: undefined, minute: undefined }) : ''}</Badge> : null}
              {d.modalidad_retencion ? <Badge variant="fiscal" data-testid="doc-modalidad">{MODALIDAD_RETENCION[d.modalidad_retencion]}</Badge> : null}
              {d.tiene_devolucion ? <Badge variant="pendiente">Con devolución</Badge> : null}
              {d.documento_origen_numero ? <Badge variant="neutro">Origen {d.documento_origen_numero}</Badge> : null}
            </div>
            <div className="grid gap-4 lg:grid-cols-3">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>{d.cliente_razon_social}</CardTitle>
                  <CardDescription>{d.cliente_rif} · {d.cliente_direccion_fiscal || 'sin dirección'} · lista {d.lista_codigo} · {veTasa ? <>tasas congeladas: BCV {formatearTasa(d.tasa_bcv)} · paralela {formatearTasa(d.tasa_paralela)}</> : <>tasa BCV congelada {formatearTasa(d.tasa_bcv)}</>}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Producto</TableHead>
                        <TableHead className="text-right">Cant.</TableHead>
                        <TableHead className="text-right">Unid.</TableHead>
                        <TableHead className="text-right">Precio u.</TableHead>
                        <TableHead className="text-right">Subtotal</TableHead>
                        <TableHead className="text-right">Entregado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {d.lineas.map((l, i) => {
                        // Combo o receta con que se vendió (spec 20): su nombre y la medida antes de su primer renglón
                        const combo = cabeceraDeCombo(d.combos, d.lineas, i);
                        return (
                          <Fragment key={l.id}>
                            {combo ? (
                              <TableRow className="bg-mg-verde-claro/60 hover:bg-mg-verde-claro/60" data-testid={`doc-combo-${combo.codigo}`}>
                                <TableCell colSpan={6} className="py-1.5">
                                  <span className="flex items-center gap-2 text-xs font-semibold"><Layers className="h-4 w-4 text-mg-verde" /> {tituloCombo(combo)}</span>
                                </TableCell>
                              </TableRow>
                            ) : null}
                            <TableRow>
                              <TableCell>
                                <p className="font-medium">{l.producto_nombre}</p>
                                <p className="text-[11px] text-muted-foreground">
                                  {l.producto_codigo} · {l.pieza_id ? (l.presentacion_nombre === 'Pieza' ? 'pieza de vidrio' : 'producto con detalle') : l.presentacion_nombre}
                                  {l.sin_inventario && !l.pieza_id ? ' · servicio' : ''}
                                  {l.de_deposito ? <span className="font-medium text-amber-700" data-testid={`doc-linea-almacen-${l.producto_codigo}`}> · sale de {l.almacen_nombre}</span> : null}
                                  {l.descuento_programado ? <span className="font-medium text-mg-verde" data-testid={`doc-linea-descuento-${l.producto_codigo}`}> · −{Money.parse(l.descuento_programado.porcentaje).toDisplay()} % {l.descuento_programado.nombre}</span> : null}
                                  {l.descuento_combo ? <span className="font-medium text-mg-verde" data-testid={`doc-linea-descuento-combo-${l.producto_codigo}`}> · −{Money.parse(l.descuento_combo).toDisplay()} % descuento del combo</span> : null}
                                </p>
                              </TableCell>
                              {/* Devoluciones parciales dejan fracciones de presentación (0,30 de un x10): se muestran solo las unidades (observación 24-09-2026) */}
                              <TableCell className="text-right num">{Number(l.cantidad) % 1 === 0 ? Money.parse(l.cantidad).toDisplay() : <span className="text-muted-foreground" title="Fracción de la presentación: ver unidades">—</span>}</TableCell>
                              <TableCell className="text-right num">{Money.parse(l.unidades).toDisplay()}</TableCell>
                              <TableCell className="text-right"><Dinero valor={l.precio_unitario} modo="triple" doc={t} bs={l.precio_unitario_bs} /></TableCell>
                              <TableCell className="text-right"><Dinero valor={l.subtotal} modo="triple" doc={t} bs={l.subtotal_bs} /></TableCell>
                              <TableCell className="text-right num">
                                {l.sin_inventario && !l.pieza_id ? <span className="text-muted-foreground">no aplica</span> : <>{Money.parse(l.entregado).toDisplay()} / {Money.parse(l.unidades).toDisplay()}</>}
                                {l.traspasado && Money.parse(l.traspasado).esPositivo() ? <span className="block text-[11px] text-amber-800" data-testid={`doc-linea-traspasado-${l.producto_codigo}`}>{Money.parse(l.traspasado).toDisplay()} pasaron a {d.sustituida_por?.numero_formateado ?? 'la nueva'}</span> : null}
                              </TableCell>
                            </TableRow>
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <div className="space-y-4">
                <Card>
                  <CardHeader><CardTitle>Totales</CardTitle></CardHeader>
                  <CardContent className="space-y-1 text-sm">
                    <p className="flex justify-between"><span>Base</span><Dinero valor={d.base} doc={t} bs={d.base_bs} /></p>
                    {Money.parse(d.descuento).esPositivo() ? <p className="flex justify-between"><span>{Money.parse(d.descuento_pct).esPositivo() ? `Descuento ${Money.parse(d.descuento_pct).toDisplay()} %` : 'Descuentos de los renglones'}</span><Dinero valor={d.descuento} doc={t} bs={d.descuento_bs} /></p> : null}
                    <p className="flex justify-between"><span>IVA {d.iva_pct}%</span><Dinero valor={d.iva} doc={t} bs={d.iva_bs} /></p>
                    <p className="flex justify-between border-t pt-1 font-semibold"><span>Total</span><Dinero valor={d.total} modo="triple" doc={t} bs={d.total_bs} /></p>
                    {Money.parse(d.retencion_iva).esPositivo() ? <p className="flex justify-between text-xs"><span>Retención IVA 75%</span><span className="num">$ {Money.parse(d.retencion_iva).toDisplay()}</span></p> : null}
                    {Money.parse(d.retencion_municipal).esPositivo() ? <p className="flex justify-between text-xs"><span>Retención municipal</span><span className="num">$ {Money.parse(d.retencion_municipal).toDisplay()}</span></p> : null}
                    <p className="flex justify-between"><span>Por cobrar</span><span className="num">$ {Money.parse(d.por_cobrar).toDisplay()}</span></p>
                    <p className="flex justify-between"><span>Pagado</span><span className="num">$ {Money.parse(d.pagado).toDisplay()}</span></p>
                    <p className="flex justify-between font-semibold"><span>Saldo</span><span className="num">$ {Money.parse(d.saldo_pendiente).toDisplay()}</span></p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle>Pagos</CardTitle></CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {d.pagos.length === 0 ? <p className="text-muted-foreground">Sin pagos.</p> : null}
                    {d.pagos.map((p) => (
                      <div key={p.id} className="rounded-lg border p-2" data-testid="doc-pago">
                        <p className="text-[11px] text-muted-foreground">{formatearFechaHora(p.recibido_en)} · {p.usuario_nombre}</p>
                        {p.lineas.map((l) => (
                          <p key={l.id} className="flex justify-between gap-2">
                            <span>
                              {(METODO_PAGO_ETIQUETA as Record<string, string>)[l.metodo] ?? l.metodo}{l.referencia ? ` · ref ${l.referencia}` : ''}
                              {l.pagador ? <span className="block text-[11px] text-mg-verde" data-testid="doc-pagador">recibido de {l.pagador}</span> : null}
                              {l.verificacion === 'pendiente' ? <Badge variant="pendiente" className="ml-1" title="La referencia no estaba entre los pagos recibidos cargados">por verificar</Badge> : null}
                              {l.verificacion === 'verificado_api' ? <Badge variant="ok" className="ml-1" title="El pago se confirmó en Banesco antes de cobrar" data-testid="pago-verificado-banesco">verificado con Banesco</Badge> : null}
                            </span>
                            <span className="num text-right">
                              {/* Lo que entregó el cliente, en su moneda; debajo, su equivalente en $ cuando pagó en Bs */}
                              {l.moneda === 'BS' ? `Bs ${Money.parse(l.monto).toDisplay()}` : `$ ${Money.parse(l.monto).toDisplay()}`}
                              {l.moneda === 'BS' ? <span className="block text-[11px] text-muted-foreground">equiv. $ {Money.parse(l.monto_usd).toDisplay()}{veTasa && l.tasa_aplicada ? ` · tasa ${formatearTasa(l.tasa_aplicada)}` : ''}</span> : null}
                            </span>
                          </p>
                        ))}
                        {Money.parse(p.vuelto_usd).esPositivo() || Money.parse(p.vuelto_bs).esPositivo() ? (
                          <p className="text-xs text-mg-ambar" data-testid="doc-pago-vuelto">
                            Vuelto $ {Money.parse(p.vuelto_usd).toDisplay()} · Bs {Money.parse(p.vuelto_bs).toDisplay()}
                            {p.vuelto_bs_metodo && p.vuelto_bs_metodo !== 'efectivo_bs' ? ` por ${METODO_VUELTO_BS_ETIQUETA[p.vuelto_bs_metodo].toLowerCase()}${p.vuelto_bs_referencia ? ` (ref ${p.vuelto_bs_referencia})` : ''}` : ''}
                          </p>
                        ) : null}
                        {Money.parse(p.sobrante_usd).esPositivo() ? <p className="text-xs text-muted-foreground" data-testid="doc-pago-sobrante">Sobrante por vuelto $ {Money.parse(p.sobrante_usd).toDisplay()}</p> : null}
                        {Money.parse(p.igtf_usd).esPositivo() ? (
                          <p className="text-xs text-amber-900" data-testid="doc-pago-igtf">
                            IGTF {Money.parse(p.igtf_pct).toString()}% sobre $ {Money.parse(p.igtf_base_usd).toDisplay()} pagados en divisas: $ {formatearExacto(p.igtf_divisa)} · Bs {Money.parse(p.igtf_bs).toDisplay()}
                          </p>
                        ) : null}
                      </div>
                    ))}
                    {/* De dónde salió el saldo a favor usado (pedido de la dueña del 22-10; solo en pantalla, informativo) */}
                    {d.saldo_origen && d.saldo_origen.length > 0 ? (
                      <div className="rounded-lg bg-muted/60 p-2 text-xs" data-testid="doc-saldo-origen">
                        <p className="font-medium">El saldo a favor usado salió de:</p>
                        {d.saldo_origen.map((o) => (
                          <p key={`${o.referencia_tipo}-${o.referencia_id}`} className="flex justify-between gap-2" data-testid="doc-saldo-origen-fila">
                            <span>
                              {o.tipo === 'abono' ? (
                                <>Abono <a href={`/imprimir/ABONO/${o.referencia_id}`} target="_blank" rel="noreferrer" className="underline">{o.numero ?? ''}</a></>
                              ) : o.tipo === 'devolucion' ? (
                                `${o.referencia_tipo === 'CAM' ? 'Cambio' : 'Nota de crédito'} ${o.numero ?? ''}`
                              ) : o.tipo === 'traspaso' ? (
                                `Traspaso del crédito de ${o.referencia_tipo === 'CAM' ? 'el Cambio' : 'la nota de crédito'} ${o.numero ?? ''}`
                              ) : (
                                'Reintegro de una retención'
                              )}
                              {' '}del {formatearFechaHora(o.fecha)}
                              {o.metodos.length > 0 ? ` · ${o.metodos.map((x) => `${(METODO_PAGO_ETIQUETA as Record<string, string>)[x.metodo] ?? x.metodo}${x.referencia ? ` ref ${x.referencia}` : ''} ${x.moneda === 'BS' ? 'Bs' : '$'} ${Money.parse(x.monto).toDisplay()}`).join(' + ')}` : ` · $ ${Money.parse(o.monto_original).toDisplay()}`}
                            </span>
                            <span className="num whitespace-nowrap">$ {Money.parse(o.monto_usado).toDisplay()}</span>
                          </p>
                        ))}
                        <p className="mt-1 text-[11px] text-muted-foreground">Se reparte en orden de llegada: lo que entró primero se usa primero. No sale en el impreso.</p>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
                {d.retenciones.length > 0 ? (
                  <Card>
                    <CardHeader><CardTitle>Retenciones</CardTitle></CardHeader>
                    <CardContent className="space-y-1 text-sm">
                      {d.retenciones.map((r) => (
                        <p key={r.id} className="flex justify-between">
                          <span>{r.tipo === 'iva' ? 'IVA 75%' : `Municipal ${r.porcentaje}%`} · <Badge variant={r.estado === 'recibida' || r.estado === 'reintegrada' ? 'ok' : 'pendiente'} data-testid="doc-retencion-estado">{(ESTADO_RETENCION_ETIQUETA as Record<string, string>)[r.estado] ?? r.estado}</Badge></span>
                          <span className="num">$ {Money.parse(r.monto).toDisplay()}</span>
                        </p>
                      ))}
                    </CardContent>
                  </Card>
                ) : null}
                {/* Archivos del documento (soportes que la caja adjunta a mano): pedido de la dueña 2026-10-19 */}
                <Card>
                  <CardContent className="pt-5">
                    <Adjuntos entidad="documento" entidadId={d.id} titulo="Archivos del documento" />
                  </CardContent>
                </Card>
                {/* Registro de correos de esta venta: cuándo, a quién, quién (o automático), si salió y qué decía */}
                <Card>
                  <CardContent className="pt-5">
                    <CorreosEnviados documentoId={d.id} />
                  </CardContent>
                </Card>
              </div>
            </div>
            {cobrando ? (
              <CobroDialog
                abierto={cobrando}
                cerrar={() => setCobrando(false)}
                // La tasa de cobro de hoy la fija el servidor (contado: la del documento; crédito: tasa 2 del día o "$ a BCV" × BCV del día)
                ctx={{ titulo: `Cobrar ${d.numero_formateado}`, porCobrar: d.saldo_pendiente, tasaBs: d.tasa_bs_cobro_hoy ?? d.tasa_aplicada, factorDivisa: '1', aplicaIgtf: d.tipo === 'FAC' || d.tipo === 'ND', topeBs: d.tipo === 'NOTA' && d.bs_notas ? Money.parse(d.bs_notas.admite_otra ? d.bs_notas.disponible_usd : '0').usdABs(d.tasa_bs_cobro_hoy ?? d.tasa_aplicada).toDocumento() : null, bsNotas: d.bs_notas, saldoFavor: cliente.data?.saldo_a_favor ?? '0', parcial: d.condicion === 'credito', clienteId: d.cliente_id }}
                alCobrar={(pagos: PagoLineaInput[], vuelto?: VueltoEntrada) =>
                  pagar.mutate(
                    { datos: { pagos, ...(vuelto ? { vuelto } : {}) }, clave },
                    // Con error la clave NO cambia (reintentar es el mismo cobro) y se relee el documento: si el pago sí quedó, se ve
                    { onSuccess: (r) => { toast.success(`Pago registrado · saldo $ ${Money.parse(r.saldo_pendiente).toDisplay()}`); setCobrando(false); }, onError: (e) => { toast.error(mensajeDeError(e)); void doc.refetch(); } },
                  )
                }
                cargando={pagar.isPending}
                ayuda={<AyudaVentana tema="documentos.cobrar" />}
              />
            ) : null}
          </>
        );
      }}
    </EstadoQuery>
  );
}
